import { createContext, useCallback, useContext, useMemo, type Dispatch } from 'react';

import type { Volume } from '~/shared/types/api';

import { filterVolumes } from '../lib/filterVolumes';
import {
  defaultExplorationUrlSnapshot,
  normalizeExplorationUrlSnapshot,
  type ExplorationUrlSnapshot,
} from '../lib/explorationUrlParams';
import { appendFilter } from '../lib/sidebar/addFilter';
import { migrateStateOnViewSwitch } from '../lib/migrateStateOnViewSwitch';
import {
  drillLocationsEqual,
  emptyDrillLocation,
  isDrillLocationEmpty,
  popDrillLocation,
} from '../lib/drill/drillLocation';
import { getGroupingModel } from '../lib/grouping';
import type {
  ExplorationAccountCache,
  ExplorationFilter,
  ExplorationHighlight,
  ExplorationSecondary,
  ExplorationSelection,
  ExplorationState,
  ExplorationVolume,
  DrillLocation,
  ViewMode,
  DisplayMode,
  CoverageGrouping,
  LoadingProgress,
} from '../types/exploration';

const emptyVolumes: ExplorationVolume[] = [];

function locationForSelection(
  state: Pick<ExplorationState, 'secondary' | 'tagKey' | 'groupByAccount' | 'view' | 'drillPath'>,
  selection: NonNullable<ExplorationSelection>
): DrillLocation | null {
  const model = getGroupingModel(state.secondary, state.tagKey);
  const opts = {
    tagKey: state.tagKey,
    groupByAccount: state.groupByAccount,
    view: state.view,
    currentLocation: state.drillPath,
  };
  if (selection.kind === 'volume') {
    return model.locationForVolume(selection.volume, opts);
  }
  const anchor = selection.vols.find((v) => v.instanceId === selection.instanceId);
  return anchor ? model.locationForInstance(anchor, selection.instanceId, opts) : null;
}

/**
 * When selection deepens/changes the canonical location (e.g. Supported → instance),
 * push the prior path onto the visit stack so canvas out / Back restore it.
 *
 * Volume selection at an unchanged location still pushes so Back/out goes
 * volume → volume → group (URL gains/loses/changes the volume param) instead
 * of leaving the group. Every volume selection — including switching from
 * one volume to another within the same group — is its own real browser
 * history entry (urlHistoryWrite: 'push'), so native Back steps through them
 * one at a time via SYNC_FROM_URL; it is not replayed from the in-memory
 * drillHistory stack, which only gates whether canvas out attempts
 * navigate(-1) at all (see BubbleCanvas.tsx's handleDrillUp).
 */
function selectionNavUpdate(
  state: ExplorationState,
  nextLocation: DrillLocation | null,
  opts?: { pushWhenLocationUnchanged?: boolean }
): Pick<
  ExplorationState,
  'drillPath' | 'drillHistory' | 'urlHistoryWrite' | 'overflowPages'
> {
  if (nextLocation == null) {
    return {
      drillPath: state.drillPath,
      drillHistory: state.drillHistory,
      urlHistoryWrite: 'replace',
      overflowPages: state.overflowPages,
    };
  }
  if (drillLocationsEqual(nextLocation, state.drillPath)) {
    if (!opts?.pushWhenLocationUnchanged) {
      return {
        drillPath: state.drillPath,
        drillHistory: state.drillHistory,
        urlHistoryWrite: 'replace',
        overflowPages: state.overflowPages,
      };
    }
    return {
      drillPath: state.drillPath,
      drillHistory: [...state.drillHistory, state.drillPath],
      urlHistoryWrite: 'push',
      overflowPages: state.overflowPages,
    };
  }
  return {
    drillPath: nextLocation,
    drillHistory: [...state.drillHistory, state.drillPath],
    urlHistoryWrite: 'push',
    // Re-rooting the canvas invalidates every `+ N More` key (they are built
    // relative to the current drill root by hierarchyCapKey), so a kept page
    // would silently expand whichever unrelated group now owns that key.
    overflowPages: {},
  };
}

/** Drop a same-location volume visit frame when selection is cleared without Back. */
function drillHistoryAfterClearingVolumeSelection(
  state: ExplorationState
): DrillLocation[] {
  if (state.selectedNode?.kind !== 'volume' || state.drillHistory.length === 0) {
    return state.drillHistory;
  }
  const top = state.drillHistory[state.drillHistory.length - 1]!;
  if (!drillLocationsEqual(top, state.drillPath)) return state.drillHistory;
  return state.drillHistory.slice(0, -1);
}

function filtersEqual(
  a: ExplorationFilter[],
  b: ExplorationFilter[]
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Push current location onto the visit stack only when filters become/stay non-empty. */
function filterNavHistory(
  state: ExplorationState,
  nextFilters: ExplorationFilter[]
): DrillLocation[] {
  if (nextFilters.length === 0) return state.drillHistory;
  return [...state.drillHistory, state.drillPath];
}

export type ExplorationAction =
  | { type: 'SET_ACCOUNT'; accountId: string | null }
  | { type: 'SET_VIEW'; view: ViewMode }
  | { type: 'SET_DISPLAY_MODE'; displayMode: DisplayMode }
  | { type: 'SET_COVERAGE_GROUPING'; coverageGrouping: CoverageGrouping }
  | { type: 'DRILL_FROM_COVERAGE'; location: DrillLocation }
  | { type: 'SET_SECONDARY'; secondary: ExplorationSecondary }
  | { type: 'SET_TAG_KEY'; tagKey: string }
  | { type: 'SET_DRILL_PATH'; location: DrillLocation; historyMode?: 'push' | 'replace' }
  | { type: 'DRILL_UP' }
  | { type: 'SET_SEARCH'; searchQuery: string }
  | { type: 'SET_FILTERS'; filters: ExplorationFilter[] }
  | { type: 'ADD_FILTER'; filter: ExplorationFilter }
  | { type: 'SET_GROUP_BY_ACCOUNT'; groupByAccount: boolean; preserveDrillPath?: boolean }
  | { type: 'EXPAND_OVERFLOW'; parentKey: string }
  | { type: 'SET_SELECTION'; selectedNode: ExplorationSelection }
  | {
      type: 'SELECT_VOLUME';
      volume: ExplorationVolume;
      /** URL hydration must not push a visit — the URL already represents this selection. */
      historyMode?: 'push' | 'none';
    }
  | { type: 'SELECT_INSTANCE'; instanceId: string; vols: ExplorationVolume[] }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_COLLAPSED'; collapsed: boolean }
  | { type: 'SET_HIGHLIGHT'; highlight: ExplorationHighlight }
  | { type: 'RESET_VIEW_STATE' }
  | { type: 'SYNC_FROM_URL'; snapshot: ExplorationUrlSnapshot }
  | { type: 'CLEAR_URL_HISTORY_WRITE' };

export const initialExplorationState: ExplorationState = {
  accountId: null,
  view: 'exploration',
  displayMode: 'bubbles',
  secondary: 'none',
  coverageGrouping: 'hierarchy',
  tagKey: '',
  drillPath: emptyDrillLocation(),
  drillHistory: [],
  urlHistoryWrite: null,
  selectedNode: null,
  searchQuery: '',
  filters: [],
  groupByAccount: false,
  overflowPages: {},
  sidebarCollapsed: false,
  highlight: null,
};

/** Align visit stack with a URL-applied location (browser Back/Forward). */
export function drillHistoryAfterUrlSync(
  history: DrillLocation[],
  currentPath: DrillLocation,
  snapshotPath: DrillLocation
): DrillLocation[] {
  for (let i = history.length - 1; i >= 0; i--) {
    if (drillLocationsEqual(history[i]!, snapshotPath)) {
      return history.slice(0, i);
    }
  }
  if (drillLocationsEqual(currentPath, snapshotPath)) {
    return history;
  }
  return [];
}

export function explorationReducer(
  state: ExplorationState,
  action: ExplorationAction
): ExplorationState {
  switch (action.type) {
    case 'SET_ACCOUNT': {
      const isAccountChange = state.accountId != null && state.accountId !== action.accountId;
      return {
        ...state,
        accountId: action.accountId,
        drillPath: isAccountChange ? emptyDrillLocation() : state.drillPath,
        drillHistory: isAccountChange ? [] : state.drillHistory,
        selectedNode: null,
        filters: isAccountChange ? [] : state.filters,
        overflowPages: {},
      };
    }
    case 'SET_VIEW': {
      return { ...state, ...migrateStateOnViewSwitch(state, action.view), drillHistory: [] };
    }
    case 'SET_DISPLAY_MODE': {
      const displayMode = normalizeExplorationUrlSnapshot({
        ...defaultExplorationUrlSnapshot,
        displayMode: action.displayMode,
      }).displayMode;
      return { ...state, displayMode, drillHistory: [] };
    }
    case 'SET_COVERAGE_GROUPING':
      return { ...state, coverageGrouping: action.coverageGrouping, drillHistory: [] };
    case 'DRILL_FROM_COVERAGE': {
      return {
        ...state,
        view: 'exploration',
        drillPath: action.location,
        drillHistory: [],
        overflowPages: {},
      };
    }
    case 'SET_SECONDARY':
      return {
        ...state,
        secondary: action.secondary,
        drillPath: emptyDrillLocation(),
        drillHistory: [],
        overflowPages: {},
      };
    case 'SET_TAG_KEY': {
      const tagUnchanged = action.tagKey === state.tagKey;
      return {
        ...state,
        tagKey: action.tagKey,
        drillPath: tagUnchanged ? state.drillPath : emptyDrillLocation(),
        drillHistory: tagUnchanged ? state.drillHistory : [],
        overflowPages: tagUnchanged ? state.overflowPages : {},
      };
    }
    case 'SET_DRILL_PATH': {
      if (drillLocationsEqual(state.drillPath, action.location)) {
        return state;
      }
      const historyMode = action.historyMode ?? 'replace';
      return {
        ...state,
        drillPath: action.location,
        drillHistory:
          historyMode === 'push' ? [...state.drillHistory, state.drillPath] : [],
        urlHistoryWrite: historyMode === 'push' ? 'push' : 'replace',
        overflowPages: {},
        selectedNode: null,
      };
    }
    case 'DRILL_UP': {
      // Canvas out with a non-empty visit stack uses navigate(-1) instead, so
      // SYNC_FROM_URL restores the full prior URL (filters included). This
      // branch is the peel fallback for shared links / empty stack, and for
      // any caller that still dispatches DRILL_UP directly.
      if (state.drillHistory.length > 0) {
        const prev = state.drillHistory[state.drillHistory.length - 1]!;
        return {
          ...state,
          drillPath: prev,
          drillHistory: state.drillHistory.slice(0, -1),
          selectedNode: null,
          overflowPages: {},
          urlHistoryWrite: 'replace',
        };
      }
      // No visit to restore (e.g. a URL-hydrated selection): clear the
      // selection first and stay on the same group/instance — only peel the
      // structural facet once nothing is selected.
      if (state.selectedNode != null) {
        return { ...state, selectedNode: null, overflowPages: {}, urlHistoryWrite: 'replace' };
      }
      if (isDrillLocationEmpty(state.drillPath)) {
        return state;
      }
      const peeled = popDrillLocation(state.drillPath);
      // Empty visit stack (shared link / breadcrumb replace): peel one hierarchy facet.
      return {
        ...state,
        drillPath: peeled,
        selectedNode: null,
        overflowPages: {},
        urlHistoryWrite: 'push',
      };
    }
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.searchQuery };
    case 'SET_FILTERS': {
      if (filtersEqual(state.filters, action.filters)) {
        return state;
      }
      const clearing = action.filters.length === 0;
      return {
        ...state,
        filters: action.filters,
        // Clearing must not push a phantom undo frame (caused extra navigate(-1) no-ops).
        drillHistory: filterNavHistory(state, action.filters),
        urlHistoryWrite: clearing ? 'replace' : 'push',
      };
    }
    case 'ADD_FILTER': {
      const nextFilters = appendFilter(state.filters, action.filter);
      if (nextFilters === state.filters) {
        return state;
      }
      return {
        ...state,
        filters: nextFilters,
        drillHistory: filterNavHistory(state, nextFilters),
        urlHistoryWrite: 'push',
      };
    }
    case 'SET_GROUP_BY_ACCOUNT':
      return {
        ...state,
        groupByAccount: action.groupByAccount,
        drillPath: action.preserveDrillPath ? state.drillPath : emptyDrillLocation(),
        drillHistory: action.preserveDrillPath ? state.drillHistory : [],
        overflowPages: action.preserveDrillPath ? state.overflowPages : {},
      };
    case 'EXPAND_OVERFLOW':
      return {
        ...state,
        overflowPages: {
          ...state.overflowPages,
          [action.parentKey]: (state.overflowPages[action.parentKey] ?? 0) + 1,
        },
      };
    case 'SET_SELECTION': {
      if (
        action.selectedNode?.kind === 'volume' &&
        state.selectedNode?.kind === 'volume' &&
        state.selectedNode.volume.ebsId === action.selectedNode.volume.ebsId
      ) {
        return state;
      }
      if (action.selectedNode == null) {
        return {
          ...state,
          selectedNode: null,
          drillHistory: drillHistoryAfterClearingVolumeSelection(state),
          sidebarCollapsed: false,
          urlHistoryWrite: 'replace',
          highlight: null,
        };
      }
      const nav = selectionNavUpdate(state, locationForSelection(state, action.selectedNode), {
        pushWhenLocationUnchanged: action.selectedNode.kind === 'volume',
      });
      return {
        ...state,
        ...nav,
        selectedNode: action.selectedNode,
        sidebarCollapsed: false,
        highlight: null,
      };
    }
    case 'SELECT_VOLUME': {
      if (
        state.selectedNode?.kind === 'volume' &&
        state.selectedNode.volume.ebsId === action.volume.ebsId
      ) {
        return state;
      }
      const selection: ExplorationSelection = { kind: 'volume', volume: action.volume };
      if (action.historyMode === 'none') {
        // URL-hydrated selection: reconcile against the volume's own canonical
        // location (still instance-scoped only if the URL's drillPath already
        // named this volume's instance) instead of trusting drillPath verbatim —
        // otherwise a stale/mismatched instance segment renders an empty group
        // with no bubble for this volume. Only write the URL when that
        // reconciliation actually changed something — the common case (e.g.
        // stepping Back through several same-group volume selections) needs
        // no correction, and forcing a write there re-pushes a duplicate URL
        // that fights the browser's own in-flight Back navigation, turning a
        // single Back press into two (volume deselects to the group first).
        const resolved = locationForSelection(state, selection) ?? state.drillPath;
        const isCorrection = !drillLocationsEqual(resolved, state.drillPath);
        return {
          ...state,
          drillPath: resolved,
          selectedNode: selection,
          sidebarCollapsed: false,
          urlHistoryWrite: isCorrection ? 'replace' : 'none',
          overflowPages: isCorrection ? {} : state.overflowPages,
          highlight: null,
        };
      }
      const nav = selectionNavUpdate(state, locationForSelection(state, selection), {
        pushWhenLocationUnchanged: true,
      });
      return {
        ...state,
        ...nav,
        selectedNode: selection,
        sidebarCollapsed: false,
        highlight: null,
      };
    }
    case 'SELECT_INSTANCE': {
      const anchor = action.vols.find((v) => v.instanceId === action.instanceId);
      if (!anchor) {
        return state;
      }
      if (
        state.selectedNode?.kind === 'instance' &&
        state.selectedNode.instanceId === action.instanceId
      ) {
        return state;
      }
      const selection: ExplorationSelection = {
        kind: 'instance',
        instanceId: action.instanceId,
        vols: action.vols,
      };
      const nextLocation = locationForSelection(state, selection);
      // Already at this instance: sidebar/highlight only — do not push/replace URL
      // (keeps volume= on the current history entry for volume undo).
      if (nextLocation != null && drillLocationsEqual(nextLocation, state.drillPath)) {
        return {
          ...state,
          selectedNode: selection,
          sidebarCollapsed: false,
          urlHistoryWrite: 'none',
          highlight: null,
        };
      }
      const nav = selectionNavUpdate(state, nextLocation);
      return {
        ...state,
        ...nav,
        selectedNode: selection,
        sidebarCollapsed: false,
        highlight: null,
      };
    }
    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedNode: null,
        drillHistory: drillHistoryAfterClearingVolumeSelection(state),
        urlHistoryWrite: 'replace',
        highlight: null,
      };
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    case 'SET_SIDEBAR_COLLAPSED':
      return { ...state, sidebarCollapsed: action.collapsed };
    case 'SET_HIGHLIGHT':
      return { ...state, highlight: action.highlight };
    case 'RESET_VIEW_STATE':
      return {
        ...state,
        drillPath: emptyDrillLocation(),
        drillHistory: [],
        selectedNode: null,
        filters: [],
        overflowPages: {},
        highlight: null,
      };
    case 'SYNC_FROM_URL': {
      const snapshot = normalizeExplorationUrlSnapshot(action.snapshot);
      const nextHistory = drillHistoryAfterUrlSync(
        state.drillHistory,
        state.drillPath,
        snapshot.drillPath
      );
      return {
        ...state,
        accountId: snapshot.accountId ?? state.accountId,
        view: snapshot.view,
        displayMode: snapshot.displayMode,
        secondary: snapshot.secondary,
        coverageGrouping: snapshot.coverageGrouping,
        tagKey: snapshot.tagKey,
        drillPath: snapshot.drillPath,
        drillHistory: nextHistory,
        urlHistoryWrite: null,
        searchQuery: snapshot.searchQuery,
        filters: snapshot.filters,
        groupByAccount: snapshot.groupByAccount,
        selectedNode: null,
        overflowPages: {},
        highlight: null,
      };
    }
    case 'CLEAR_URL_HISTORY_WRITE':
      return state.urlHistoryWrite == null ? state : { ...state, urlHistoryWrite: null };
    default:
      return state;
  }
}

export interface ExplorationDataContextValue {
  accounts: Record<string, ExplorationAccountCache>;
  apiVolumeByEbsId: Record<string, Volume>;
  /** True during auth wait, initial volume fetch, or background refetch. */
  loading: boolean;
  /** True only before first successful volume load — used to hide sidebar on first paint. */
  initialLoading: boolean;
  loadingProgress: LoadingProgress | null;
  error: string | null;
  refresh: () => void;
}

export const ExplorationStateContext = createContext<ExplorationState | null>(null);
export const ExplorationDispatchContext =
  createContext<Dispatch<ExplorationAction> | null>(null);
export const ExplorationDataContext =
  createContext<ExplorationDataContextValue | null>(null);

export function useExplorationState() {
  const ctx = useContext(ExplorationStateContext);
  if (!ctx)
    throw new Error(
      'useExplorationState must be used within ExplorationRootProvider'
    );
  return ctx;
}

export function useExplorationDispatch() {
  const ctx = useContext(ExplorationDispatchContext);
  if (!ctx)
    throw new Error(
      'useExplorationDispatch must be used within ExplorationRootProvider'
    );
  return ctx;
}

export function useExplorationData() {
  const ctx = useContext(ExplorationDataContext);
  if (!ctx)
    throw new Error('useExplorationData must be used within ExplorationRootProvider');
  return ctx;
}

export function useExplorationActions() {
  const dispatch = useExplorationDispatch();
  return useMemo(
    () => ({
      setAccount: (accountId: string | null) => dispatch({ type: 'SET_ACCOUNT', accountId }),
      setView: (view: ViewMode) => dispatch({ type: 'SET_VIEW', view }),
      setDisplayMode: (displayMode: DisplayMode) =>
        dispatch({ type: 'SET_DISPLAY_MODE', displayMode }),
      setCoverageGrouping: (coverageGrouping: CoverageGrouping) =>
        dispatch({ type: 'SET_COVERAGE_GROUPING', coverageGrouping }),
      drillFromCoverage: (location: DrillLocation) =>
        dispatch({ type: 'DRILL_FROM_COVERAGE', location }),
      setSecondary: (secondary: ExplorationSecondary) =>
        dispatch({ type: 'SET_SECONDARY', secondary }),
      setTagKey: (tagKey: string) => dispatch({ type: 'SET_TAG_KEY', tagKey }),
      setDrillPath: (
        location: DrillLocation,
        opts?: { historyMode?: 'push' | 'replace' }
      ) =>
        dispatch({
          type: 'SET_DRILL_PATH',
          location,
          historyMode: opts?.historyMode ?? 'replace',
        }),
      drillUp: () => dispatch({ type: 'DRILL_UP' }),
      setSearch: (searchQuery: string) => dispatch({ type: 'SET_SEARCH', searchQuery }),
      setFilters: (filters: ExplorationFilter[]) =>
        dispatch({ type: 'SET_FILTERS', filters }),
      addFilter: (filter: ExplorationFilter) => dispatch({ type: 'ADD_FILTER', filter }),
      setGroupByAccount: (groupByAccount: boolean) =>
        dispatch({ type: 'SET_GROUP_BY_ACCOUNT', groupByAccount }),
      expandOverflow: (parentKey: string) => dispatch({ type: 'EXPAND_OVERFLOW', parentKey }),
      selectVolume: (volume: ExplorationVolume) =>
        dispatch({ type: 'SELECT_VOLUME', volume }),
      selectInstance: (
        instanceId: string,
        vols: import('../types/exploration').ExplorationVolume[]
      ) => dispatch({ type: 'SELECT_INSTANCE', instanceId, vols }),
      clearSelection: () => dispatch({ type: 'CLEAR_SELECTION' }),
      toggleSidebar: () => dispatch({ type: 'TOGGLE_SIDEBAR' }),
      setSidebarCollapsed: (collapsed: boolean) =>
        dispatch({ type: 'SET_SIDEBAR_COLLAPSED', collapsed }),
      setHighlight: (highlight: ExplorationHighlight) =>
        dispatch({ type: 'SET_HIGHLIGHT', highlight }),
    }),
    [dispatch]
  );
}

export function useCurrentAccountVolumes() {
  const state = useExplorationState();
  const { accounts } = useExplorationData();
  if (!state.accountId) return emptyVolumes;
  return accounts[state.accountId]?.volumes ?? emptyVolumes;
}

export function useFilteredVolumes() {
  const volumes = useCurrentAccountVolumes();
  const state = useExplorationState();
  return useMemo(
    () => filterVolumes(volumes, state.filters, state.searchQuery),
    [volumes, state.filters, state.searchQuery]
  );
}
